import posthog from 'posthog-js';
import { StarCraftLecture } from '../../../types/types';

// ============================================================
// Admin API 함수 — 백엔드 /api/admin/* (routes/admin.routes.ts)와 1:1. 함수 이름은 `admin` + 동작 + 대상.
//
// [보안]
// - 비밀번호(토큰)는 sessionStorage에만 저장 (탭 닫으면 소멸)
// - localStorage 절대 사용 금지
// - API 요청마다 Authorization 헤더로 전송
//
// [TOC — 함수명으로 grep해서 이동]
//   토큰 보관        getAdminToken, setAdminToken, clearAdminToken
//   탭 간 동기화     requestAdminTokenFromOtherTabs (BroadcastChannel), verifyAdminToken
//   강의             adminFetchLectures, adminFetchLectureById, adminPatchLecture, adminFetchRelatedLectures
//   동의어           adminFetchSynonyms / Create / Update / Delete, adminFetchUncoveredSynonyms, adminValidateSynonymCanonical
//   영문 레이블      adminFetchTagLabels / Create / Update / Delete, adminFetchUncoveredTagLabels
//   전략 종족·가중치 adminFetchStrategyRaces / … , adminFetchStrategyWeights / …
//   선수             adminFetchPlayers / Create / Update / Delete, adminFetchUncoveredPlayers, adminFetchCoveredPlayers
//   외부 조회        adminYoutubeLookup, adminSoopLookup
//   운영·리포트      adminFetchSearchStats, adminAnnounce, adminFetchOpponentScoreUnresolved, adminFetchPositionUnresolved,
//                    adminFetchZeroResultSearches, adminFetchMissingMatchGroup, adminFetchNonCanonicalUsage
//   맵               adminFetchMaps, adminFetchMapPositions, adminCreateMap / Update / Delete
//   맵 파일          adminFetchMapFiles, adminUploadMapFile, adminDeleteMapFile (원본 .scm/.scx 보관함 — 미리보기 렌더는 노트북 스크립트)
//   심시티 사진      adminFetchSimcity, adminUploadSimcity, adminUpdateSimcity, adminDeleteSimcity (맵·위치·종족·자리별 건물 배치 캡처, 2026-10-22)
//   팁               adminFetchTips / Create / Update / Delete
//   전략 동기화      adminSyncStrategies, adminUndoStrategySync
//   언급된 경기      adminFetchLectureRefs, adminCreateLectureRef, adminDeleteLectureRef
//   래더(실험)       adminFetchLadderGames, adminFetchLadderGameDetail
//
// 각 그룹의 응답 타입(interface)은 그 그룹 함수 바로 위에 선언돼 있다.
// ============================================================

const getApiUrl = () =>
  process.env.REACT_APP_API_URL || 'http://127.0.0.1:8787';

export type SynonymCategory = 'tag' | 'map' | 'player' | 'strategy' | 'matchup';

export interface AdminLectureSummary {
  id: number;
  title: string;
  link: string;
  thumbnail: string;
  channel_name: string;
  pov: string;
  tags: string[];
  map: string[];
  player_a: { name: string; race: string; strategy: string[] };
  player_b: { name: string; race: string; strategy: string[] };
  upload_date: string;
  match_group_id: number | null;
  opponent_score: number | null;
}

export interface AdminLectureListResponse {
  lectures: AdminLectureSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface LecturePatchPayload {
  tags?: string[];
  map?: string[];
  pov?: 'playerA' | 'playerB' | 'observer';
  playerA?: {
    name?: string;
    race?: string;
    strategy?: string[];
    position?: string | null;
  };
  playerB?: {
    name?: string;
    race?: string;
    strategy?: string[];
    position?: string | null;
  };
  title?: string;
  duration?: string;
  uploadDate?: string;
  matchGroupId?: number | null;
  result?: 'A' | 'B' | 'draw' | null;
  opponentScore?: number | null;
}

/**
 * 세션스토리지에서 Admin 토큰을 안전하게 가져옴
 */
export const getAdminToken = (): string | null => {
  try {
    return sessionStorage.getItem('_adm_tk');
  } catch {
    return null;
  }
};

/**
 * 실패 응답 본문을 파싱해 사람이 읽을 수 있는 에러 메시지로 조립.
 * 관리페이지는 운영자 본인만 보는 도구라 오류를 뭉뚱그리지 않고 그대로 노출한다(2026-09-09
 * 사용자 요청) — 백엔드 catch 블록의 `detail`(예: D1 한도 초과 원문)까지 붙여서 반환.
 */
const buildAdminErrorMessage = async (res: Response): Promise<string> => {
  const err = await res.json().catch(() => null) as any;
  const detail = err?.detail ? ` — ${err.detail}` : '';
  return `${err?.error || '서버 오류'} (${res.status})${detail}`;
};

/**
 * Admin 토큰을 세션스토리지에 저장 (탭 닫히면 자동 소멸)
 */
export const setAdminToken = (token: string): void => {
  try {
    sessionStorage.setItem('_adm_tk', token);
  } catch {
    console.error('sessionStorage 사용 불가');
  }
  // 어드민 로그인한 브라우저 = 운영자 본인 — 이후 모든 이벤트에 is_admin 마킹 (지표 오염 필터용)
  // register는 localStorage에 남아 토큰 만료 후에도 이 브라우저의 이벤트를 계속 구분해줌
  try {
    posthog.register({ is_admin: true });
    // GA도 본인 트래픽으로(index.html이 다음 방문부터 traffic_type=internal로 보냄)
    localStorage.setItem('_self_traffic', '1');
  } catch {}
};

/**
 * Admin 토큰 제거 (로그아웃)
 */
export const clearAdminToken = (): void => {
  try {
    sessionStorage.removeItem('_adm_tk');
  } catch {}
};

// ============================================================
// 탭 간 인증 동기화 (BroadcastChannel, 2026-09-16)
//
// window.open()으로 연 새 탭은 opener 관계 덕분에 sessionStorage를 자동 상속하지만(AdminPage의
// "강의로 가기" 주석 참고), 사용자가 직접 새 창/새 탭을 열어 /admin에 들어간 경우는 opener가 없어
// 세션스토리지가 비어있는 채로 시작됨 — 사용자가 "새창일때 인증 안 이어짐"으로 신고.
// localStorage는 여전히 안 쓴다(위 "절대 사용 금지" 규칙 유지) — 대신 같은 브라우저에 이미
// 로그인된 다른 탭이 "지금" 열려있으면 그 탭에게 메모리 상에서 토큰을 물어봐서 받아온다.
// 모든 탭이 닫히면 이 채널도 같이 사라지므로 "탭 닫으면 소멸" 성질은 그대로 유지됨.
// ============================================================
const AUTH_CHANNEL_NAME = 'starinfo-admin-auth-sync';

const getAuthChannel = (): BroadcastChannel | null => {
  try {
    return new BroadcastChannel(AUTH_CHANNEL_NAME);
  } catch {
    return null; // BroadcastChannel 미지원 브라우저 — 조용히 기능 생략, 기존 동작으로 폴백
  }
};

// 다른 탭이 토큰을 물어보면 내가 가진 토큰으로 응답 (모듈 로드 시 1회 등록)
const responderChannel = getAuthChannel();
if (responderChannel) {
  responderChannel.onmessage = (e: MessageEvent) => {
    if (e.data?.type === 'request-token') {
      const token = getAdminToken();
      if (token) responderChannel.postMessage({ type: 'token', token });
    }
  };
}

/**
 * 같은 브라우저에 이미 로그인된 다른 탭이 있으면 그 토큰을 받아온다 (최대 300ms 대기).
 * 없으면 null — 로그인 화면을 그대로 보여주면 됨.
 */
export const requestAdminTokenFromOtherTabs = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const channel = getAuthChannel();
    if (!channel) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => {
      channel.close();
      resolve(null);
    }, 300);
    channel.onmessage = (e: MessageEvent) => {
      if (e.data?.type === 'token' && e.data.token) {
        clearTimeout(timer);
        channel.close();
        resolve(e.data.token);
      }
    };
    channel.postMessage({ type: 'request-token' });
  });
};

/**
 * 인증 유효성 검사 (서버에 헬스체크 요청)
 * 토큰이 맞는지 확인하는 용도.
 * ok=false일 때 reason으로 원인을 구분 — 'unauthorized'(진짜 비번 틀림)와 'network'(요청 자체가
 * 실패, 예: CORS 차단·서버 다운)를 섞으면 진짜 비번이 맞는데도 "비밀번호 틀림"으로 오인하게 됨
 * (2026-09-04 LAN 접속 시 ALLOWED_ORIGINS 누락으로 실제 겪은 문제).
 */
export const verifyAdminToken = async (token: string): Promise<{ ok: boolean; reason?: 'unauthorized' | 'network'; detail?: string }> => {
  try {
    const res = await fetch(`${getApiUrl()}/api/admin/lectures?limit=1`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.status === 200 ? { ok: true } : { ok: false, reason: 'unauthorized', detail: `HTTP ${res.status}` };
  } catch (err: any) {
    // 브라우저가 CORS/네트워크 실패를 자세히 안 알려줘서(보안상 의도된 동작) 여기선
    // err.message가 사실상 유일한 단서 — 보통 "Failed to fetch"(Chrome) 정도만 나옴.
    return { ok: false, reason: 'network', detail: err?.message || String(err) };
  }
};

/**
 * Admin 강의 목록 조회
 */
export const adminFetchLectures = async (
  page: number = 1,
  limit: number = 30,
  searchQuery: string = ''
): Promise<AdminLectureListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (searchQuery.trim()) {
    params.set('q', searchQuery.trim());
  }

  const res = await fetch(
    `${getApiUrl()}/api/admin/lectures?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));

  return res.json();
};

/**
 * 래더 전략 분석용 본인 리플레이 파싱 결과(2026-10-05) — BuildAnalysis의 BuildGame 모양 그대로
 */
export const adminFetchLadderGames = async <T,>(): Promise<T[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/ladder-games`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).games;
};

/** 경기 하나의 빌드 타임라인(시뮬레이션 결과) — 경기 카드를 펼칠 때만 받는다 */
export const adminFetchLadderGameDetail = async <T,>(id: string): Promise<T | null> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/ladder-game?id=${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).detail;
};

/**
 * 동의어 목록 조회
 */
export interface Synonym {
  id: number;
  canonical_name: string;
  alias: string;
  category: string;
}

export interface SynonymListResponse {
  synonyms: Synonym[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchSynonyms = async (
  page: number = 1,
  limit: number = 50,
  q: string = '',
  category: string = ''
): Promise<SynonymListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (q.trim()) params.set('q', q.trim());
  if (category.trim()) params.set('category', category.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/synonyms?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminAnnounce = async (message: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/announce`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminCreateSynonym = async (
  canonical_name: string,
  alias: string,
  category: string
): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/synonyms`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ canonical_name, alias, category }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminUpdateSynonym = async (
  id: number,
  canonical_name: string,
  alias: string,
  category: string
): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/synonyms/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ canonical_name, alias, category }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminFetchUncoveredSynonyms = async (
  category: string
): Promise<{ items: Array<{ name: string; count: number }>; total: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(
    `${getApiUrl()}/api/admin/synonyms/uncovered?category=${encodeURIComponent(category)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminValidateSynonymCanonical = async (
  name: string,
  category: string = ''
): Promise<{ isAlias: boolean; realCanonical: string | null }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ name });
  if (category.trim()) params.set('category', category.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/synonyms/validate?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminDeleteSynonym = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/synonyms/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ── 영문 레이블 API ──

export interface TagLabel {
  id: number;
  canonical_name: string;
  category: string;
  en: string;
}

export interface TagLabelListResponse {
  labels: TagLabel[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchTagLabels = async (
  page: number = 1,
  limit: number = 50,
  q: string = '',
  category: string = ''
): Promise<TagLabelListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (q.trim()) params.set('q', q.trim());
  if (category.trim()) params.set('category', category.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/tag-labels?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminCreateTagLabel = async (
  canonical_name: string,
  category: string,
  en: string
): Promise<{ id: number | null }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tag-labels`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ canonical_name, category, en }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const data = await res.json().catch(() => ({ id: null }));
  return { id: data.id ?? null };
};

export const adminUpdateTagLabel = async (id: number, en: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tag-labels/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ en }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteTagLabel = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tag-labels/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminFetchUncoveredTagLabels = async (
  category: string
): Promise<{ items: Array<{ name: string; count: number }>; total: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(
    `${getApiUrl()}/api/admin/tag-labels/uncovered?category=${encodeURIComponent(category)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── 전략 종족 라벨 API ──
// (상세검색 A/B 자동배정 features/search/lib/assignDetailedTag.ts가 쓰는
//  data/strategyRaceInference.ts는 이 테이블에서 생성됨 — apps/backend/scripts/strategy-races/export.ts)

export interface StrategyRace {
  id: number;
  canonical_name: string;
  race: "T" | "P" | "Z" | "COMMON" | null;
  source: "reviewed" | "inferred" | "unclassified";
}

export interface StrategyRaceListResponse {
  items: StrategyRace[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchStrategyRaces = async (
  page: number = 1,
  limit: number = 50,
  q: string = '',
  source: string = ''
): Promise<StrategyRaceListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (q.trim()) params.set('q', q.trim());
  if (source.trim()) params.set('source', source.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-races?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminCreateStrategyRace = async (
  canonical_name: string,
  race: string
): Promise<{ id: number | null }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-races`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ canonical_name, race }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const data = await res.json().catch(() => ({ id: null }));
  return { id: data.id ?? null };
};

export const adminUpdateStrategyRace = async (id: number, race: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-races/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ race }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteStrategyRace = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-races/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export interface StrategyWeight {
  id: number;
  canonical_name: string;
  weight: number;
}

export interface StrategyWeightListResponse {
  items: StrategyWeight[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchStrategyWeights = async (
  page: number = 1,
  limit: number = 50,
  q: string = ''
): Promise<StrategyWeightListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (q.trim()) params.set('q', q.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-weights?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminCreateStrategyWeight = async (
  canonical_name: string,
  weight: number
): Promise<{ id: number | null }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-weights`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ canonical_name, weight }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const data = await res.json().catch(() => ({ id: null }));
  return { id: data.id ?? null };
};

export const adminUpdateStrategyWeight = async (id: number, weight: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-weights/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ weight }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteStrategyWeight = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-weights/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ── 선수 API ──

export interface Player {
  name: string;
  race: string | null;
  player_type: string | null;
  country: string | null;
  is_female: number;
  is_asl_participant: number;
  channel_name: string | null;
  youtube_handle: string | null;
  nickname: string | null;
  /** 강의 등장 횟수(player_a/b 합산) — "최다출현순" 정렬용 */
  appearance_count: number;
}

export interface PlayerListResponse {
  players: Player[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchPlayers = async (
  page: number = 1,
  limit: number = 50,
  q: string = '',
  sort: string = 'name-asc'
): Promise<PlayerListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit), sort });
  if (q.trim()) params.set('q', q.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/players?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminCreatePlayer = async (
  name: string,
  race: string | null,
  playerType: string,
  country: string | null,
  isFemale: number,
  isAslParticipant: number,
  channelName: string | null,
  youtubeHandle: string | null
): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/players`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, race, player_type: playerType, country, is_female: isFemale, is_asl_participant: isAslParticipant, channel_name: channelName, youtube_handle: youtubeHandle }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminUpdatePlayer = async (
  name: string,
  race: string | null,
  playerType: string,
  country: string | null,
  isFemale: number,
  isAslParticipant: number,
  channelName: string | null,
  youtubeHandle: string | null
): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/players/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ race, player_type: playerType, country, is_female: isFemale, is_asl_participant: isAslParticipant, channel_name: channelName, youtube_handle: youtubeHandle }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeletePlayer = async (name: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/players/${encodeURIComponent(name)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminFetchUncoveredPlayers = async (): Promise<{ items: Array<{ name: string; count: number; suggested_channel: string | null }>; total: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/players/uncovered`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminFetchCoveredPlayers = async (): Promise<{ items: Array<{ name: string; count: number; country: string | null; player_type: string | null }>; total: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/players/covered`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export interface YoutubeChannelResult {
  channelId: string;
  title: string;
  handle: string | null;
}

export const adminYoutubeLookup = async (q: string): Promise<YoutubeChannelResult[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/youtube/lookup?q=${encodeURIComponent(q)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export interface SoopMeta {
  title: string;
  channelName: string;
  thumbnailUrl: string;
  duration: string;
}

export const adminSoopLookup = async (videoId: string): Promise<SoopMeta> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/soop/lookup?videoId=${encodeURIComponent(videoId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export interface RelatedLecture {
  id: number;
  title: string;
  link: string;
  player_a_name: string;
  player_b_name: string;
  player_a_race: string;
  player_b_race: string;
  player_a_position: string | null;
  player_b_position: string | null;
  map_name: string | null;
  upload_datetime: string | null;
  pov: string;
  result: string | null;
  match_group_id: number | null;
  strategy_a: string[];
  strategy_b: string[];
  tags: string[];
}

export interface RelatedLecturesResponse {
  target: RelatedLecture;
  related: RelatedLecture[];
}

export const adminFetchRelatedLectures = async (id: number): Promise<RelatedLecturesResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lectures/${id}/related`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

/**
 * 검색 통계 (인기 태그·검색어, 0건 검색, n-패턴 fallback 히트)
 */
export interface SearchStats {
  period: string;
  totalSearches: number;
  zeroResultTotal: number;
  topTags: { tag: string; count: number }[];
  topQueries: { query: string; count: number }[];
  zeroResults: { search: string; count: number; lastAt: string }[];
  fallbackTotal: number;
  fallbackHits: { hit: string; count: number }[];
  countries: { country: string; count: number }[];
  recentLogs: { tags: string[]; searchQuery: string | null; resultCount: number; createdAt: string; country: string | null }[];
}

export const adminFetchSearchStats = async (days: number, limit = 30): Promise<SearchStats> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/search-stats?days=${days}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── opponent_score 진짜 미확인 리포트 API ──

export interface OpponentScoreUnresolvedItem {
  id: number;
  title: string;
  link: string;
  player_a_name: string;
  player_b_name: string;
  upload_date: string | null;
}

export interface OpponentScoreUnresolvedResponse {
  items: OpponentScoreUnresolvedItem[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchOpponentScoreUnresolved = async (
  page: number = 1,
  limit: number = 50
): Promise<OpponentScoreUnresolvedResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const res = await fetch(`${getApiUrl()}/api/admin/opponent-score-unresolved?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── 시작 위치 미태깅 리포트 API ──

export interface PositionUnresolvedItem {
  id: number;
  title: string;
  link: string;
  player_a_name: string;
  player_b_name: string;
  player_a_position: string | null;
  player_b_position: string | null;
  upload_date: string | null;
}

export interface PositionUnresolvedResponse {
  items: PositionUnresolvedItem[];
  total: number;
  page: number;
  limit: number;
}

export const adminFetchPositionUnresolved = async (
  page: number = 1,
  limit: number = 50
): Promise<PositionUnresolvedResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const res = await fetch(`${getApiUrl()}/api/admin/position-unresolved?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── maps API ──

export interface MapMeta {
  map_name: string;
  player_count: number | null;
  is_island: number | null;
  has_island_expansion: number | null;
  has_backyard: number | null;
  terrain: 'flat' | 'hill' | 'reverse_hill' | null;
  has_center_multi: number | null;
  has_neutral_assimilator: number | null;
  is_ladder_map: number | null;
  starting_positions: string | null;
  lecture_count: number;
}

export interface MapMetaListResponse {
  maps: MapMeta[];
  total: number;
  page: number;
  limit: number;
}

export interface MapMetaPayload {
  player_count?: number | null;
  is_island?: number | null;
  has_island_expansion?: number | null;
  has_backyard?: number | null;
  terrain?: string | null;
  has_center_multi?: number | null;
  has_neutral_assimilator?: number | null;
  is_ladder_map?: number | null;
  starting_positions?: string | null;
}

export const adminFetchMaps = async (
  page: number = 1,
  limit: number = 50,
  q: string = ''
): Promise<MapMetaListResponse> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (q.trim()) params.set('q', q.trim());

  const res = await fetch(`${getApiUrl()}/api/admin/maps?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// mapName -> starting_positions 토큰 배열("1시,5시,7시,11시" 파싱됨). starting_positions가 아직
// 미검수(NULL)인 맵은 응답에 아예 빠짐 — 소비 측(LecturesTab)에서 fallback 처리.
export const adminFetchMapPositions = async (): Promise<Record<string, string[]>> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/maps/positions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const data = await res.json();
  return data.positions;
};

export const adminCreateMap = async (mapName: string, payload: MapMetaPayload): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/maps`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ map_name: mapName, ...payload }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminUpdateMap = async (mapName: string, payload: MapMetaPayload): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/maps/${encodeURIComponent(mapName)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteMap = async (mapName: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/maps/${encodeURIComponent(mapName)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ─── 팁(로딩 화면·검색 0건 화면용 잡학) CRUD ──────────────────────────────────

export interface BwTip {
  id: number;
  text: string;
  created_at: string;
  category: string | null;
  textEn: string | null; // 영문 번역(2026-10-18) — drizzle select()라 camelCase로 옴
}

export const adminFetchTips = async (): Promise<BwTip[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tips`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    const detail = err?.detail ? ` — ${err.detail}` : '';
    throw new Error(`${err?.error || '서버 오류'} (${res.status})${detail}`);
  }
  const data = await res.json();
  return data.tips;
};

export const adminCreateTip = async (text: string, category: string | null): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tips`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, category }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminUpdateTip = async (id: number, text: string, category: string | null, textEn: string | null): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tips/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, category, textEn }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteTip = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/tips/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

/**
 * 강의 부분 수정 (PATCH)
 */
export const adminPatchLecture = async (
  id: number,
  payload: LecturePatchPayload
): Promise<{ message: string; autoPropagatedTo?: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lectures/${id}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));

  return res.json();
};

// ── 0건 검색어 리포트 API ──

export interface ZeroResultSearchItem {
  tags: string;
  search_query: string;
  occurrences: number;
  last_seen: string;
}

export const adminFetchZeroResultSearches = async (
  page: number = 1,
  limit: number = 50
): Promise<{ items: ZeroResultSearchItem[]; total: number; page: number; limit: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const res = await fetch(`${getApiUrl()}/api/admin/zero-result-searches?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── "동일 경기 영상 존재" 태그 있는데 match_group_id 없는 강의 리포트 API ──

export interface MissingMatchGroupItem {
  id: number;
  title: string;
  link: string;
  player_a_name: string;
  player_b_name: string;
  upload_date: string | null;
}

export const adminFetchMissingMatchGroup = async (
  page: number = 1,
  limit: number = 50
): Promise<{ items: MissingMatchGroupItem[]; total: number; page: number; limit: number }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const res = await fetch(`${getApiUrl()}/api/admin/missing-match-group?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── 기존 강의 데이터 중 대표어 아닌 별칭 표기 감사 리포트 API ──

export interface NonCanonicalUsageItem {
  category: 'tag' | 'strategy' | 'map' | 'player';
  value: string;
  canonical: string;
  occurrences: number;
}

export const adminFetchNonCanonicalUsage = async (): Promise<{ items: NonCanonicalUsageItem[] }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/non-canonical-usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

// ── "동일 경기 영상 존재" 그룹 내 전략 태그 동기화(얕은복사/병합) API ──

export interface StrategySyncResult {
  success: boolean;
  message?: string;
  logId?: number;
  addedA?: string[];
  addedB?: string[];
  /** 한쪽이라도 canonical 기준 교집합이 없으면 채워짐 — "정말 같은 경기 맞아?" 경고. */
  warning?: string;
}

/** dryRun=true면 DB에 아무것도 안 쓰고 미리보기만 계산해서 돌려준다(confirm 문구용) —
 * 실제 반영은 dryRun 생략(또는 false)으로 한 번 더 호출. */
export const adminSyncStrategies = async (targetId: number, sourceId: number, dryRun?: boolean): Promise<StrategySyncResult> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lectures/${targetId}/sync-strategies`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sourceId, dryRun }),
  });

  if (res.status === 401) throw new Error('UNAUTHORIZED');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail ? ` — ${data.detail}` : '';
    throw new Error(`${data.error || data.message || '서버 오류'} (${res.status})${detail}`);
  }
  return data;
};

export const adminUndoStrategySync = async (logId: number): Promise<{ success: boolean; message?: string }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/strategy-sync-log/${logId}/undo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) throw new Error('UNAUTHORIZED');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail ? ` — ${data.detail}` : '';
    throw new Error(`${data.error || data.message || '서버 오류'} (${res.status})${detail}`);
  }
  return data;
};

// ── 강의 간 레퍼런스 연결("언급된 경기") ──

export interface LectureRefItem {
  id: number;
  from_lecture_id: number;
  to_lecture_id: number;
  created_at: string;
  to_title: string | null;
  to_link: string | null;
}

// 연결 전 대상 ID 미리보기용 — 어드민 강의 단건 조회 재사용(존재하지 않으면 404).
export const adminFetchLectureById = async (id: number): Promise<StarCraftLecture> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lectures/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminFetchLectureRefs = async (fromLectureId: number): Promise<LectureRefItem[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lecture-refs?fromId=${fromLectureId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const data = await res.json();
  return data.refs;
};

export const adminCreateLectureRef = async (
  fromLectureId: number,
  toLectureId: number
): Promise<{ message: string; toTitle?: string }> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lecture-refs`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fromLectureId, toLectureId }),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return res.json();
};

export const adminDeleteLectureRef = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');

  const res = await fetch(`${getApiUrl()}/api/admin/lecture-refs/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ── 맵 파일(.scm/.scx) 보관함(2026-10-21) — 백엔드 mapFile.service. 업로드만 하고 미리보기 렌더는
// 노트북의 scripts/maps/render-previews.ts가 한다(워커에선 타일셋·sharp를 못 돌림). renderedAt이 null이면 렌더 대기
export interface MapFileEntry {
  mapName: string;
  fileName: string;
  size: number;
  uploadedAt: string;
  renderedAt: string | null;
}

export const adminFetchMapFiles = async (): Promise<MapFileEntry[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/maps/files`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).files;
};

export const adminUploadMapFile = async (mapName: string, file: File): Promise<MapFileEntry> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/maps/files/${encodeURIComponent(mapName)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) },
    body: file,
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).entry;
};

// 맵 페이지 "맵 받기"(2026-10-22, 어드민 전용 — 사용자 결정으로 공개 다운로드 경로 없음). 토큰을 실어 받아 원래 파일명(X-File-Name)으로 저장.
// variant: 같은 맵팩의 관전·안개·관전+안개·트레이닝 판(ob|fog|obfog|tr), 없으면 원본
export const adminDownloadMapFile = async (mapName: string, variant?: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const q = variant ? `?variant=${variant}` : '';
  const res = await fetch(`${getApiUrl()}/api/admin/maps/files/${encodeURIComponent(mapName)}${q}`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  const fileName = decodeURIComponent(res.headers.get('X-File-Name') ?? '') || `${mapName}.scx`;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const adminDeleteMapFile = async (mapName: string): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/maps/files/${encodeURIComponent(mapName)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ── 심시티 사진(2026-10-22) — 백엔드 simcity.service(메타 D1 simcity_images, 바이트 KV). 베타 동안 목록·업로드·수정·삭제 전부 어드민 전용.
// 사진 자체는 /api/simcity/image/:key로 공개 서빙(키는 난수) — <img src>용 simcityImageUrl은 features/map/api.ts
export interface SimcityImage {
  id: number;
  imageKey: string;
  mapName: string;
  position: string;
  race: 'T' | 'Z' | 'P';
  spot: 'main' | 'natural' | 'other';
  matchup: string | null;
  title: string;
  /** 전략 태그(0059, 2026-10-22) — 없으면 빈 배열 */
  tags: string[];
  note: string | null;
  lectureId: number | null;
  sourceUrl: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  status: 'visible' | 'hidden';
  createdAt: string;
}
export interface SimcityUploadPayload {
  mapName: string;
  position: string;
  race: 'T' | 'Z' | 'P';
  spot: 'main' | 'natural' | 'other';
  matchup: string | null;
  title: string;
  tags: string[];
  note: string | null;
  lectureId: number | null;
  sourceUrl: string | null;
  width: number;
  height: number;
  imageBase64: string; // webp, data: 접두어 없이
}

export const adminFetchSimcity = async (mapName?: string): Promise<SimcityImage[]> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/simcity${mapName ? `?map=${encodeURIComponent(mapName)}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).items;
};

export const adminUploadSimcity = async (payload: SimcityUploadPayload): Promise<SimcityImage> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/simcity`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).item;
};

export const adminUpdateSimcity = async (id: number, patch: Partial<Pick<SimcityImage, 'title' | 'tags' | 'note' | 'sourceUrl' | 'lectureId' | 'status' | 'matchup'>>): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/simcity/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

// ── 앞마당 위치 사람 확인(2026-10-22) — 백엔드 routes/mapNaturals.routes.ts(KV map_naturals_review:v1)
export interface NaturalReview {
  /** skip = 모르겠음(반영 때 계산값 그대로) */
  status: 'ok' | 'fixed' | 'skip';
  naturals: { clock: string; x: number; y: number }[];
  /** 트리플(같은 탭에서 함께 확인) */
  thirds?: { clock: string; x: number; y: number }[];
  /** 뒷마당(사람이 찍은 것만) */
  backyards?: { clock: string; x: number; y: number }[];
  at: string;
}
export const adminFetchNaturalReviews = async (): Promise<Record<string, NaturalReview>> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/map-naturals`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
  return (await res.json()).reviews;
};
/** status 없이 보내면 그 맵 확인 취소 */
export const adminSaveNaturalReview = async (map: string, review: { status?: NaturalReview['status']; naturals?: NaturalReview['naturals']; thirds?: NaturalReview['naturals']; backyards?: NaturalReview['naturals'] }): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/map-naturals/${encodeURIComponent(map)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(review),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};

export const adminDeleteSimcity = async (id: number): Promise<void> => {
  const token = getAdminToken();
  if (!token) throw new Error('UNAUTHORIZED');
  const res = await fetch(`${getApiUrl()}/api/admin/simcity/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(await buildAdminErrorMessage(res));
};
